import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Icon, Tooltip, Badge } from 'antd';
import moment from 'moment';
import cn from 'classnames';
import { declOfNum, dspxDiffDays } from '../../../helpers/dspx-utils';
import CellDataContainer from '../CellDataContainer/CellDataContainer';
import CellText from '../CellText/CellText';
import executeTimeActions from '../../../actions/execute-time.actions';
import { setItemToCalendar } from '../../../actions/itemToCalendar';
import {
  setRequestDrawerVisible,
  setSelectedRequestId,
} from '../../../actions/requestCardActions';
import styles from './RequestExecutionDate.module.scss';
import getRequestCalendarType from '../../../requests/getRequestCalendarType';
import { calendarTypeIds } from '../../../constants/requests.constants';
import { setSelectedRequest } from '../../../actions/acceptanceRequestToCalendarActions';
import Priority from '../RequestPriority/RequestPriority';
import ExecuteDateWithActions from './ui/ExecuteDateWithActions/ExecuteDateWithActions';

const RequestExecutionDate = ({
  request = {},
  getPopupContainer = () => document.body,
  calendarIcon = true,
  onCalendarClick,
  onPriorityChange,
  isStatistics,
  canChange = true,
}) => {
  const history = useHistory();

  const dispatch = useDispatch();

  const requestDrawerVisible = useSelector(
    (state) => state.requestCardStore.requestDrawerVisible
  );

  const delayPeriod = request.doneDate
    ? dspxDiffDays(request.doneDate, request.executeTime)
    : dspxDiffDays(0, request.executeTime);

  const executeTime = request.executeTime ? '' : '/ Нет даты назначения';

  const delayPeriodText = `${delayPeriod} ${declOfNum(delayPeriod, [
    ' день',
    ' дня',
    ' дней',
  ])} ${executeTime}`;

  const overdueTooltipText = `Просроченно на ${
    delayPeriod > 0 ? delayPeriodText : '?'
  }`;

  const calendarTooltipText = request.taskId
    ? `${moment(request.taskStart).format('DD.MM.YYYY HH:mm')} - ${moment(
        request.taskEnd
      ).format('HH:mm')}`
    : 'Добавить в календарь';

  const isActive = canChange && !request.paidServiceRequestId;

  const handleCalendarClick = async (e) => {
    e.stopPropagation();

    const calendarType = await getRequestCalendarType({
      requestId: request.id,
      requestParentServiceId: request.parentServiceId,
    });

    if (calendarType === calendarTypeIds.acceptance) {
      dispatch(setSelectedRequest(request));
    } else {
      if (requestDrawerVisible) {
        dispatch(setRequestDrawerVisible(false));
        dispatch(setSelectedRequestId(null));
      }

      dispatch(setItemToCalendar(request));

      const searchParams = new URLSearchParams();

      searchParams.set('requestId', request.id);

      history.push({
        pathname: '/Calendar',
        search: searchParams.toString(),
      });
    }

    if (typeof onCalendarClick === 'function') {
      onCalendarClick();
    }
  };

  useEffect(() => {
    return () => dispatch(executeTimeActions.closeCalendarModal());
  }, []);

  return (
    <CellDataContainer>
      <div className={styles.container}>
        <ExecuteDateWithActions
          request={request}
          onPriorityOk={({ requestId, priority }) =>
            onPriorityChange(requestId, priority)
          }
          isStatistics={isStatistics}
          canChange={isActive}
        />
        {isActive && request.executer && calendarIcon && (
          <Tooltip
            title={calendarTooltipText}
            getPopupContainer={getPopupContainer}
          >
            <Icon
              type="calendar"
              className={cn(styles.calendarIcon, {
                [styles.active]: request.taskId,
              })}
              onClick={handleCalendarClick}
            />
          </Tooltip>
        )}
        <Priority
          record={request}
          onOk={
            canChange
              ? ({ requestId, priority }) =>
                  onPriorityChange(requestId, priority)
              : undefined
          }
          iconStyle={{
            color: '#0995ed',
            cursor: 'pointer',
            marginRight: '8px',
          }}
          isStatistics={isStatistics}
        />
        {!isNaN(delayPeriod) && delayPeriod > 0 && request.statusId !== 4 && (
          <Tooltip
            title={overdueTooltipText}
            getPopupContainer={getPopupContainer}
          >
            <Badge
              count={
                <Icon
                  type="fire"
                  theme="twoTone"
                  className={styles.fireIcon}
                  twoToneColor={delayPeriod > 14 ? '#ff0003' : '#f44336'}
                />
              }
            >
              <div
								style={{
									top: '4px',
									right: '4px',
									backgroundColor: '#d8f2ff',
									color: '#0d6aa2',
									borderRadius: '12px',
									padding: '4px 8px',
									fontSize: '12px',
									textAlign: 'center',
									cursor: 'default',
									minWidth: '20px',
									display: 'inline-block',
									cursor: 'pointer'
								}}
							>
								{delayPeriod > 0 ? delayPeriod : '?'}
							</div>
            </Badge>
          </Tooltip>
        )}
      </div>
      {request.doneDate && (
        <Tooltip
          title="Выполнена"
          getPopupContainer={getPopupContainer}
        >
          <div className={cn(styles.container)}>
            <svg className={cn('icon', 'icon-clipboard', styles.done)}>
              <use xlinkHref="#icon-clipboard"></use>
            </svg>
            <CellText className={styles.done}>
              {moment(request.doneDate).format('DD.MM.YYYY HH:mm')}
            </CellText>
          </div>
        </Tooltip>
      )}

      {request.closeDate && (
        <Tooltip
          title="Закрыта"
          getPopupContainer={getPopupContainer}
        >
          <div className={cn(styles.container)}>
            <Icon type="close-square" />
            {request.closeDateTime && (
              <CellText>
                {moment(request.closeDateTime).format('DD.MM.YYYY HH:mm')}
              </CellText>
            )}
          </div>
        </Tooltip>
      )}
    </CellDataContainer>
  );
};

export default RequestExecutionDate;
